<template>
  <div class="layout-wrap">
    <!-- ══ Sidebar ══ -->
    <nav class="sidebar">
      <div class="sidebar-logo">
        <div class="logo-info">
          <div class="logo-name">CIKON 沁尔康</div>
        </div>
      </div>

      <div class="nav-list">
        <div
          class="nav-item"
          :class="{ active: activeKey === 'home' }"
          @click="goHome"
        >
          <i class="el-icon-s-home nav-icon" />
          <span class="nav-label">首页</span>
        </div>

        <div
          v-for="item in filteredTopMenus"
          :key="item.key"
          class="nav-item"
          :class="{ active: activeKey === item.key, 'panel-open': panelKey === item.key }"
          @mouseenter="onNavEnter(item.key, $event)"
          @mouseleave="onNavLeave"
        >
          <i :class="[item.icon, 'nav-icon']" />
          <span class="nav-label">{{ item.label }}</span>
          <i class="el-icon-arrow-right nav-arrow" />
        </div>
      </div>

      <div class="sidebar-footer">
        <el-dropdown placement="right-end" @command="handleSidebarCommand">
          <div class="user-bar">
            <div class="user-avatar">{{ avatarInitial }}</div>
            <span class="user-name">{{ displayName }}</span>
            <i class="el-icon-arrow-right" style="font-size:11px;opacity:.5;" />
          </div>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">个人中心</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </nav>

    <!-- ══ Mega panel ══ -->
    <transition name="panel-slide">
      <div
        v-if="panelKey && filteredMegaConfig[panelKey]"
        ref="megaPanel"
        class="mega-panel"
        :style="panelStyle"
        @mouseenter="onPanelEnter"
        @mouseleave="onPanelLeave"
      >
        <div class="panel-head">
          <i :class="currentPanelMenu && currentPanelMenu.icon" class="panel-head-icon" />
          <span>{{ currentPanelMenu && currentPanelMenu.label }}</span>
        </div>
        <div class="panel-body">
          <div
            v-for="group in filteredMegaConfig[panelKey].groups"
            :key="group.title"
            class="panel-group"
          >
            <div class="group-title">
              <span class="group-bar" />{{ group.title }}
            </div>
            <div class="group-items">
              <a
                v-for="child in group.items"
                :key="child"
                class="panel-item"
                @click="onItemClick(child)"
              >{{ child }}</a>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ Main ══ -->
    <div class="main-wrap" @mouseenter="onMainEnter">
      <!-- Top bar -->
      <header class="top-bar">
        <div class="top-bar-left">
          <span class="system-name">沁尔康智饮云管理系统</span>
        </div>
        <div class="top-bar-right">
          <div class="search-box" ref="searchBox" :class="{ 'is-active': searchDropdownVisible }">
            <i class="el-icon-search search-icon" />
            <input
              v-model="searchQuery"
              class="search-input"
              placeholder="搜索栏目..."
              @input="onSearchInput"
              @keydown.down.prevent="onSearchArrowDown"
              @keydown.up.prevent="onSearchArrowUp"
              @keydown.enter="onSearchEnter"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
            />
            <div v-if="searchDropdownVisible && searchResults.length" class="search-dropdown">
              <template v-if="!searchQuery.trim()">
                <div class="search-tip">请输入搜索关键字，或直接选择下方栏目</div>
                <div v-for="group in groupedSearchResults" :key="group.label">
                  <div class="search-group-title">{{ group.label }}</div>
                  <div
                    v-for="item in group.items"
                    :key="item.path"
                    :class="['search-item', { active: searchHighlight === item._index }]"
                    @mousedown.prevent="selectSearchResult(item)"
                  >
                    <span class="search-item-name">{{ item.name }}</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                  v-for="(item, i) in searchResults"
                  :key="item.path"
                  :class="['search-item', { active: searchHighlight === i }]"
                  @mousedown.prevent="selectSearchResult(item)"
                >
                  <span class="search-item-name">{{ item.name }}</span>
                  <span class="search-item-group">{{ item.group }}</span>
                </div>
              </template>
            </div>
            <div v-else-if="searchDropdownVisible && searchQuery.trim() && !searchResults.length" class="search-dropdown">
              <div class="search-empty">未找到匹配栏目</div>
            </div>
          </div>
          <el-popover
            v-if="showMessageBell"
            placement="bottom-end"
            :width="360"
            trigger="click"
            popper-class="message-popover"
            @show="onBellPopoverShow"
          >
            <template #reference>
              <el-badge :value="msgUnreadTotal" :hidden="msgUnreadTotal === 0" class="tb-badge">
                <i class="el-icon-bell tb-icon" title="消息通知" />
              </el-badge>
            </template>
            <div class="msg-pop-header">
              <span>消息通知</span>
              <el-badge v-if="msgUnreadTotal > 0" :value="msgUnreadTotal" type="danger" />
            </div>
            <div class="msg-pop-list" v-loading="msgPopoverLoading">
              <div v-if="!msgRecentList.length && !msgPopoverLoading" class="msg-pop-empty">暂无未读消息</div>
              <div
                v-for="item in msgRecentList"
                :key="item.id"
                class="msg-pop-item"
                @click="onPopoverMsgClick(item)"
              >
                <div class="msg-pop-dot" />
                <div class="msg-pop-body">
                  <div class="msg-pop-content">{{ item.content }}</div>
                  <div class="msg-pop-time">{{ formatDateTime(item.createTime) }}</div>
                </div>
              </div>
            </div>
            <div class="msg-pop-footer" @click="goMessageList">查看全部消息</div>
          </el-popover>
          <i class="el-icon-question tb-icon" title="帮助中心" style="margin-left:12px;" />
          <el-divider direction="vertical" />
          <el-dropdown @command="handleTopbarCommand">
            <div class="user-dropdown">
              <div class="user-avatar-sm">{{ avatarInitial }}</div>
              <span class="user-label">{{ displayName }}</span>
              <i class="el-icon-arrow-down" style="font-size:11px;margin-left:4px;" />
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <!-- Page tab bar -->
      <div class="page-tabs" ref="pageTabs">
        <div
          v-for="tab in openTabs"
          :key="tab.path"
          :class="['page-tab', { active: activeTab === tab.path }]"
          @click="switchTab(tab)"
          @contextmenu.prevent="openCtxMenu($event, tab)"
        >
          <i v-if="tab.icon" :class="tab.icon" style="margin-right:4px;" />
          <span>{{ tab.label }}</span>
          <i
            v-if="tab.closable"
            class="el-icon-close tab-close"
            @click.stop="closeTab(tab)"
          />
        </div>
      </div>

      <!-- Content -->
      <main class="content-area">
        <div class="content-area-inner">
          <router-view v-slot="{ Component, route }">
            <keep-alive :include="keepAliveNames">
              <component
                :is="Component"
                v-if="Component"
                :key="pageInstanceKey(route)"
              />
            </keep-alive>
          </router-view>
        </div>
      </main>
    </div>

    <!-- Tab context menu (at root level to avoid overflow clipping) -->
    <div
      v-if="ctxMenu.visible"
      class="tab-ctx-menu"
      :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }"
      @mousedown.stop
      @contextmenu.stop.prevent
    >
      <div class="ctx-item" @click="ctxRefresh"><i class="el-icon-refresh" />刷新</div>
      <div
        v-if="ctxMenu.tab && ctxMenu.tab.closable"
        class="ctx-item"
        @click="ctxCloseCurrent"
      ><i class="el-icon-close" />关闭</div>
      <div class="ctx-item" @click="ctxCloseOthers"><i class="el-icon-circle-close" />关闭其他</div>
      <div class="ctx-item" @click="ctxCloseAll"><i class="el-icon-error" />关闭所有</div>
    </div>

    <!-- 个人中心弹窗 -->
    <el-dialog title="个人中心" v-model="profileVisible" width="440px" append-to-body>
      <el-descriptions :column="1" border size="default">
        <el-descriptions-item label="员工姓名">{{ profileInfo.empName }}</el-descriptions-item>
        <el-descriptions-item label="员工编号">{{ profileInfo.empCode }}</el-descriptions-item>
        <el-descriptions-item label="登录账号">{{ profileInfo.loginAccount }}</el-descriptions-item>
        <el-descriptions-item label="所属部门">{{ profileInfo.deptName }}</el-descriptions-item>
        <el-descriptions-item label="角色">{{ profileInfo.roleNames }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ profileInfo.phone }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="profileInfo.status === 'enabled' ? 'success' : 'danger'" size="small">
            {{ profileInfo.status === 'enabled' ? '正常' : '禁用' }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" v-model="pwdDialogVisible" width="460px" append-to-body :close-on-click-modal="false">
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="90px" size="default">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" placeholder="请输入原密码" show-password />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" placeholder="8-20位，含字母、数字和特殊符号" show-password />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" placeholder="再次输入新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="default" @click="pwdDialogVisible = false">取消</el-button>
        <el-button type="primary" size="default" :loading="pwdLoading" @click="submitChangePassword">确认修改</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getCurrentUser, changePassword, logout } from '@/api/auth'
import { canAccessRoute, canViewMenuItem, getUserPermissionIds } from '@/utils/permissionMap'
import { getUnreadCount, getRecentUnread, markAsRead } from '@/api/messageApi'
import { formatDateTime } from '@/utils/date'
import { clearListViewStateByTabPath } from '@/utils/listViewState'

/** 最多同时打开的 Tab 数量 */
const MAX_TABS = 50

/** 侧边栏菜单项 → 路由映射表，搜索导航和菜单跳转共用 */
const MENU_ROUTE_MAP = {
  '设备/滤芯/配件': '/main/product/list',
  '服务包产品': '/main/product/service-package/list',
  // '组合产品': '/main/product/bundle/list',
  '水量包产品': '/main/product/water-package/list',
  '产品分类': '/main/product/category',
  '产品系列': '/main/product/series',
  '产品单位': '/main/product/unit',
  // '设备类型管理': '/main/product/device-type',
  '设备电源功率管理': '/main/product/device-power-supply',
  '设备水箱容量管理': '/main/product/device-tank-capacity',
  '设备龙头组合管理': '/main/product/device-faucet-combo',
  '产品标签': '/main/product/tag',
  '产品品牌': '/main/product/brand',
  '服务包权益': '/main/product/service-package-benefit',
  '滤芯类型': '/main/product/filter-type',
  // '产品规格': '/main/product/spec',
  '用户列表': '/main/user/list',
  '用户等级': '/main/user/level',
  // '用户类别': '/main/user/category',
  '用户行业': '/main/user/industry',
  '用户标签': '/main/user/tag',
  '用户性质': '/main/user/property',
  '用户来源渠道': '/main/user/source-channel',
  '经销商列表': '/main/channel/list',
  '合同管理': '/main/channel/contract',
  '经销商解约管理': '/main/channel/termination',
  '经销商等级': '/main/channel/level',
  '经销商权益': '/main/channel/benefit',
  '经销商类别': '/main/channel/category',
  '经销商来源渠道': '/main/channel/source-channel',
  '经销商行业': '/main/channel/industry',
  '经销商性质': '/main/channel/property',
  '部门与员工': '/main/system/department',
  '角色管理': '/main/system/role',
  '账号管理': '/main/system/account',
  '登录日志': '/main/system/login-log',
  '操作日志': '/main/system/operation-log',
  '目标设置': '/main/system/target-settings',
  '区域管理': '/main/system/area',
  '大区管理': '/main/system/large-area',
  '其他设置': '/main/system/other-settings',
  '通知设置': '/main/system/message-config',
  '资金账户': '/main/finance/fund-account',
  '经销商充值申请': '/main/finance/dealer-recharge-application',
  '预扣款': '/main/finance/pre-deduction',
  '提现申请': '/main/finance/withdrawal-application',
  '资金流水': '/main/finance/fund-flow',
  '对账单': '/main/finance/monthly-statement',
  '发票': '/main/finance/invoice',
  '线索报备': '/main/report/lead',
  '服务工单': '/main/workorder/service',
  '售后申请管理': '/main/workorder/aftersales-manage',
  '投诉管理': '/main/workorder/complaint-manage',
  '工程师管理': '/main/workorder/engineer',
  '工程师等级': '/main/workorder/engineer-level',
  '执行人管理': '/main/workorder/dispatcher',
  '服务科目': '/main/workorder/subject',
  '工单标签': '/main/workorder/tag',
  '投诉类别管理': '/main/workorder/complaint-category',
  '售后类别管理': '/main/workorder/aftersale-category',
  // '租赁业务分析': '/main/report/lease-business-analysis',
  // '经销商业绩': '/main/report/dealer-performance',
  // '销售业绩': '/main/report/sales-performance',
  '资金收支': '/main/report/fund-income-expenditure',
  '回款统计表': '/main/report/v2/payment-collection',
  '装机统计表': '/main/report/v2/installation-stats',
  '销售统计表': '/main/report/v2/sales-stats',
  '员工统计表': '/main/report/v2/employee-stats',
  '经销商分析表': '/main/report/v2/dealer-analysis',
  '设备销售统计表': '/main/report/v2/device-sales-stats',
  '运维统计表': '/main/report/v2/ops-statistics',
  '租赁设备保有量分析': '/main/report/v2/lease-device-stock',
  '服务包保有量分析': '/main/report/v2/service-pack-stock',
  '合约管理': '/main/iot/contract',
  '解约原因管理': '/main/iot/termination-reason',
  '设备管理': '/main/iot/device',
  '协议查询': '/main/iot/protocol',
  '指令日志': '/main/iot/log',
  '审批流程设置': '/main/order/approval-flow',
  '订单设置': '/main/order/settings',
  '充值订单': '/main/order/recharge/list',
  '取消订单': '/main/order/cancel/list',
  '退换货订单': '/main/order/after-sales-return/list',
  '产品订单': '/main/order/product/list',
  // '组合产品订单': '/main/order/bundle-product/list',
  '服务包订单': '/main/order/service-package/list',
  '服务包订单发货申请': '/main/order/sp-shipment-application/list',
  '销售出库单明细': '/main/order/sales-outbound/list',
  '实时大屏': '/screen/realtime',
  '演示大屏': '/screen/demo',
}

export default {
  name: 'MainLayout',
  provide() {
    return {
      isOpenMainTab: (path) => this.openTabs.some(t => t.path === path),
      closeMainTab: (path) => this.closeTabByPathSilent(path),
    }
  },
  data() {
    return {
      activeKey: 'home',
      activeTab: '/main/dashboard',
      openTabs: [
        { label: '首页', path: '/main/dashboard', icon: 'el-icon-s-home', closable: false },
      ],
      /** 按 tab.path 递增，用于关闭/刷新后丢弃 keep-alive 缓存实例 */
      pageRefreshMap: {},
      ctxMenu: { visible: false, x: 0, y: 0, tab: null },
      panelKey: null,
      navItemRect: null,
      closeTimer: null,
      topMenus: [
        { key: 'screen',    label: '大屏',   icon: 'el-icon-monitor' },
        { key: 'product',   label: '产品',   icon: 'el-icon-goods' },
        { key: 'order',     label: '订单',   icon: 'el-icon-document' },
        { key: 'user',      label: '用户',   icon: 'el-icon-user' },
        { key: 'channel',   label: '经销商', icon: 'el-icon-s-custom' },
        { key: 'report_lead', label: '报备', icon: 'el-icon-s-flag' },
        { key: 'iot',       label: '物联网',   icon: 'el-icon-connection' },
        { key: 'workorder', label: '售后工单',   icon: 'el-icon-s-claim' },
        { key: 'finance',   label: '财务',   icon: 'el-icon-s-finance' },
        { key: 'report',    label: '经营报表', icon: 'el-icon-data-analysis' },
        { key: 'system',    label: '系统设置', icon: 'el-icon-setting' },
      ],
      megaConfig: {
        screen: {
          groups: [
            { title: '大屏', items: ['实时大屏', '演示大屏'] },
          ],
        },
        product: {
          groups: [
            /* 隐藏：组合产品 */
            { title: '产品',     items: ['设备/滤芯/配件', '服务包产品', /* '组合产品', */ '水量包产品'] },
            {
              title: '辅助资料',
              items: [
                '产品分类',
                '产品系列',
                '产品单位',
                /* '设备类型管理', */
                '设备电源功率管理',
                '设备水箱容量管理',
                '设备龙头组合管理',
                '产品标签',
                '产品品牌',
                '服务包权益',
                '滤芯类型',
                /* '产品规格', */
              ],
            },
          ],
        },
        order: {
          groups: [
            /* 隐藏：组合产品订单 */
            { title: '订单', items: ['产品订单', /* '组合产品订单', */ '服务包订单', '服务包订单发货申请', '销售出库单明细'] },
            { title: '续费充值', items: [/* 入口隐藏：租赁续费订单 */ '充值订单'] },
            { title: '未安装前售后', items: ['退换货订单', '取消订单'] },
            { title: '设置', items: ['审批流程设置','订单设置']},
          ],
        },
        user: {
          groups: [
            { title: '用户', items: ['用户列表', '用户等级', /* 入口隐藏：用户类别 */ '用户标签','用户行业','用户性质', '用户来源渠道'] },
          ],
        },
        channel: {
          groups: [
            { title: '经销商', items: ['经销商列表', '经销商解约管理'] },
            { title: '辅助资料', items: ['经销商等级', '经销商权益', '经销商类别', '经销商来源渠道','经销商行业','经销商性质'] },
          ],
        },
        report_lead: {
          groups: [
            { title: '报备', items: ['线索报备'] },
          ],
        },
        finance: {
          groups: [
            { title: '资金', items: ['资金账户', '经销商充值申请', '预扣款', '资金流水', '对账单', '提现申请'] },
            { title: '发票', items: ['发票'] },
          ],
        },
        workorder: {
          groups: [
            { title: '售后工单', items: ['服务工单', '售后申请管理', '投诉管理'] },
            { title: '售后配置', items: ['工程师管理', '工程师等级', '执行人管理', '服务科目', '工单标签', '投诉类别管理', '售后类别管理'] },
          ],
        },
        iot: {
          groups: [
            { title: '合约', items: ['合约管理', '解约原因管理'] },
            { title: '设备', items: ['设备管理', '协议查询', '指令日志'] },
          ],
        },
        report: {
          groups: [
            /* 删除旧报表
            { title: '经营报表', items: ['销售业绩', '经销商业绩', '租赁业务分析'] },
            */
            {
              title: '经营报表',
              items: [
                '回款统计表',
                '装机统计表',
                '销售统计表',
                '员工统计表',
                '经销商分析表',
                '设备销售统计表',
                '运维统计表',
                '租赁设备保有量分析',
                '服务包保有量分析',
                '资金收支',
              ],
            },
          ],
        },
        system: {
          groups: [
            { title: '系统', items: ['部门与员工', '角色管理', '账号管理'] },
            { title: '日志', items: ['登录日志', '操作日志'] },
            { title: '基础设置', items: ['目标设置', '商城管理', '区域管理', '大区管理', '通知设置', '其他设置'] },
          ],
        },
      },
      // 目录搜索
      searchQuery: '',
      searchResults: [],
      searchDropdownVisible: false,
      searchHighlight: -1,
      userInfo: {},
      profileVisible: false,
      profileInfo: {},
      pwdDialogVisible: false,
      pwdLoading: false,
      pwdForm: { oldPassword: '', newPassword: '', confirmPassword: '' },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 8, max: 20, message: '密码长度8-20位', trigger: 'blur' },
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: (rule, value, callback) => {
            if (value !== this.pwdForm.newPassword) callback(new Error('两次密码不一致'))
            else callback()
          }, trigger: 'blur' },
        ],
      },
      // ── 消息通知 ──
      msgUnreadTotal: 0,
      msgRecentList: [],
      msgPopoverLoading: false,
      msgPollTimer: null,
    }
  },
  created() {
    this.loadUserInfo()
    this.refreshPermissions()
    this.loadMsgUnreadCount()
    this.msgPollTimer = setInterval(() => this.loadMsgUnreadCount(), 60000)
  },
  beforeUnmount() {
    if (this.msgPollTimer) clearInterval(this.msgPollTimer)
  },
  computed: {
    /**
     * 与路由组件 name 对齐；勿对同一 Component 挂两个实例，否则 Vue 3.5 会读到 null.exposed
     * 列表页仅在对应 Tab 仍打开时纳入 keep-alive，关闭后丢弃实例
     */
    keepAliveNames() {
      const names = ['ProductOrderFormPage']
      if (this.openTabs.some(t => t.path === '/main/order/product/list')) names.push('ProductOrderListPage')
      if (this.openTabs.some(t => t.path === '/main/order/service-package/list')) names.push('ServicePackageOrderListPage')
      if (this.openTabs.some(t => t.path === '/main/order/cancel/list')) names.push('CancelOrderListPage')
      if (this.openTabs.some(t => t.path === '/main/product/list')) names.push('ProductListPage')
      if (this.openTabs.some(t => t.path === '/main/product/service-package/list')) names.push('ServicePackageListPage')
      if (this.openTabs.some(t => t.path === '/main/product/water-package/list')) names.push('WaterPackageListPage')
      // 产品添加/编辑页：切 tab 回来保留已填内容；页签关闭且已离开该路由后，随 include 变化被 prune 丢弃
      // 判据同时看 $route 与 openTabs：$route 保证首次进入即被缓存，openTabs 保证切到别的 tab 期间缓存不被回收
      const isProductFormPath = p => p === '/main/product/add' || /^\/main\/product\/edit\/[^/]+$/.test(p)
      if (isProductFormPath(this.$route.path) || this.openTabs.some(t => isProductFormPath(t.path))) {
        names.push('ProductFormPage')
      }
      return names
    },
    /** 当前用户权限 ID 列表 */
    userPermIds() {
      return getUserPermissionIds()
    },
    /** 消息铃铛可见性：与消息列表页权限保持一致（无权限则隐藏铃铛） */
    showMessageBell() {
      return canAccessRoute('/main/system/message-list')
    },
    /** 按权限过滤后的侧边栏顶级菜单 */
    filteredTopMenus() {
      const permIds = this.userPermIds
      return this.topMenus.filter(menu => {
        const config = this.megaConfig[menu.key]
        if (!config) return true // 无子项配置，默认显示
        // 有子项：至少有一个子项对用户可见
        return config.groups.some(group =>
          group.items.some(itemName => canViewMenuItem(itemName, permIds))
        )
      })
    },
    /** 按权限过滤后的 Mega 面板配置 */
    filteredMegaConfig() {
      const permIds = this.userPermIds
      const result = {}
      for (const [key, config] of Object.entries(this.megaConfig)) {
        const filteredGroups = config.groups
          .map(group => ({
            ...group,
            items: group.items.filter(itemName => canViewMenuItem(itemName, permIds)),
          }))
          .filter(group => group.items.length > 0)
        if (filteredGroups.length > 0) {
          result[key] = { ...config, groups: filteredGroups }
        }
      }
      return result
    },
    currentMenu() {
      return this.topMenus.find(m => m.key === this.activeKey)
    },
    currentPanelMenu() {
      return this.topMenus.find(m => m.key === this.panelKey)
    },
    panelStyle() {
      if (!this.navItemRect || this.navItemRect._top == null) return {}
      return { top: this.navItemRect._top + 'px' }
    },
    searchIndex() {
      const items = []
      for (const [panelKey, config] of Object.entries(this.filteredMegaConfig)) {
        const menu = this.topMenus.find(m => m.key === panelKey)
        const groupLabel = menu ? menu.label : panelKey
        for (const group of config.groups) {
          for (const itemName of group.items) {
            const path = MENU_ROUTE_MAP[itemName]
            if (path) {
              items.push({ name: itemName, path, group: groupLabel })
            }
          }
        }
      }
      return items
    },
    groupedSearchResults() {
      const groups = []
      let index = 0
      for (const item of this.searchResults) {
        let group = groups.find(g => g.label === item.group)
        if (!group) {
          group = { label: item.group, items: [] }
          groups.push(group)
        }
        group.items.push({ ...item, _index: index++ })
      }
      return groups
    },
    displayName() {
      return this.userInfo.empName || '未登录'
    },
    avatarInitial() {
      const name = this.userInfo.empName || ''
      return name.charAt(0) || '?'
    },
  },
  watch: {
    activeTab() {
      // 激活 Tab 变化时，自动横向滚动到可视区域（Tab 数量较多时）
      this.$nextTick(() => this.scrollActiveTabIntoView())
    },
    openTabs() {
      this.$nextTick(() => this.scrollActiveTabIntoView())
    },
    $route: {
      handler(to) {
        const prevTab = this.activeTab
        const tabPath = this.tabPathForRoute(to)
        if (to.path === '/main/product/add') {
          this.addTab(to.query && to.query.copyFrom ? '复制添加产品' : '添加产品', tabPath)
        } else if (/^\/main\/product\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑产品', to.path)
        } else if (/^\/main\/product\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('产品详情', to.path)
        } else if (/^\/main\/system\/role\/[^/]+\/permission$/.test(to.path)) {
          this.addTab('分配权限', to.path)
        } else if (to.path === '/main/system/employee/target') {
          this.addTab('业务目标设置', to.path)
        } else if (to.path === '/main/system/target-settings') {
          this.addTab('目标设置', to.path)
        } else if (to.path === '/main/system/other-settings') {
          this.addTab('其他设置', to.path)
        } else if (to.path === '/main/system/area') {
          this.addTab('区域管理', to.path)
        } else if (to.path === '/main/system/large-area') {
          this.addTab('大区管理', to.path)
        } else if (to.path === '/main/system/login-log') {
          this.addTab('登录日志', to.path)
        } else if (to.path === '/main/system/operation-log') {
          this.addTab('操作日志', to.path)
        } else if (to.path === '/main/finance/fund-account') {
          this.addTab('资金账户', to.path)
        } else if (to.path === '/main/finance/dealer-recharge-application') {
          this.addTab('经销商充值申请', to.path)
        } else if (to.path === '/main/finance/pre-deduction') {
          this.addTab('预扣款', to.path)
        } else if (to.path === '/main/finance/withdrawal-application') {
          this.addTab('提现申请', to.path)
        } else if (to.path === '/main/finance/fund-flow') {
          this.addTab('资金流水', to.path)
        } else if (to.path === '/main/finance/invoice') {
          this.addTab('发票列表', to.path)
        } else if (to.path === '/main/finance/monthly-statement') {
          this.addTab('对账单', to.path)
        } else if (to.path === '/main/user/list') {
          this.addTab('用户列表', to.path)
        } else if (to.path === '/main/user/create') {
          this.addTab('创建用户', to.path)
        } else if (/^\/main\/user\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑用户', to.path)
        } else if (/^\/main\/user\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('用户详情', to.path)
        } else if (/^\/main\/user\/[^/]+\/contract\/[^/]+$/.test(to.path)) {
          this.addTab('合约详情', to.path)
        } else if (/^\/main\/user\/[^/]+\/contract-traffic\/[^/]+$/.test(to.path)) {
          this.addTab('流量型合约详情', to.path)
        } else if (/^\/main\/user\/[^/]+\/contract-service\/[^/]+$/.test(to.path)) {
          this.addTab('服务包合约详情', to.path)
        } else if (to.path === '/main/channel/list') {
          this.addTab('经销商列表', to.path)
        } else if (to.path === '/main/channel/create') {
          this.addTab('创建经销商', to.path)
        } else if (/^\/main\/channel\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑经销商', to.path)
        } else if (/^\/main\/channel\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('经销商详情', to.path)
        } else if (to.path === '/main/channel/contract') {
          this.addTab('合同管理', to.path)
        } else if (/^\/main\/channel\/contract\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('合同详情', to.path)
        } else if (to.path === '/main/channel/termination') {
          this.addTab('经销商解约管理', to.path)
        } else if (to.path === '/main/report/lead') {
          this.addTab('线索报备', to.path)
        /* 删除旧报表
        } else if (to.path === '/main/report/lease-business-analysis') {
          this.addTab('租赁业务分析', to.path)
        } else if (to.path === '/main/report/dealer-performance') {
          this.addTab('经销商业绩', to.path)
        } else if (to.path === '/main/report/sales-performance') {
          this.addTab('销售业绩', to.path)
        */
        } else if (to.path === '/main/report/fund-income-expenditure') {
          this.addTab('资金收支', to.path)
        } else if (to.meta && to.meta.dictKey && to.meta.title) {
          this.addTab(to.meta.title, to.path)
        } else if (to.path.startsWith('/main/report/v2/') && to.meta && to.meta.title) {
          this.addTab(to.meta.title, to.path)
        } else if (to.path === '/main/product/service-package/list') {
          this.addTab('服务包产品', to.path)
        } else if (to.path === '/main/product/service-package/add') {
          this.addTab(to.query && to.query.copyFrom ? '复制创建服务包' : '创建服务包', tabPath)
        } else if (/^\/main\/product\/service-package\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑服务包', to.path)
        } else if (/^\/main\/product\/service-package\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('服务包详情', to.path)
        } else if (to.path === '/main/product/bundle/list') {
          this.addTab('组合产品', to.path)
        } else if (to.path === '/main/product/bundle/add') {
          this.addTab('创建组合产品', to.path)
        } else if (/^\/main\/product\/bundle\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑组合产品', to.path)
        } else if (to.path === '/main/product/water-package/list') {
          this.addTab('水量包产品', to.path)
        } else if (to.path === '/main/product/water-package/add') {
          this.addTab('添加充值方案', to.path)
        } else if (/^\/main\/product\/water-package\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑充值方案', to.path)
        } else if (/^\/main\/product\/water-package\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('充值方案详情', to.path)
        } else if (/^\/main\/product\/workorder-price\/\d+$/.test(to.path)) {
          this.addTab('工单服务价格', to.path)
        } else if (/^\/main\/product\/link-device\/[^/]+$/.test(to.path)) {
          this.addTab('关联设备', to.path)
        } else if (to.path === '/main/order/approval-flow') {
          this.addTab('审批流程设置', to.path)
        } else if (to.path === '/main/order/approval-flow/add') {
          this.addTab('创建审批流', to.path)
        } else if (/^\/main\/order\/approval-flow\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑审批流', to.path)
        } else if (to.path === '/main/order/settings') {
          this.addTab('订单设置', to.path)
        } else if (to.path === '/main/order/recharge/list') {
          this.addTab('充值订单', to.path)
        } else if (/^\/main\/order\/recharge\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('充值订单详情', to.path)
        } else if (to.path === '/main/order/lease-renewal/list') {
          this.addTab('租赁续费订单', to.path)
        } else if (/^\/main\/order\/lease-renewal\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('续费订单详情', to.path)
        } else if (to.path === '/main/order/cancel/list') {
          this.addTab('取消订单', to.path)
        } else if (/^\/main\/order\/cancel\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('取消订单详情', to.path)
        } else if (to.path === '/main/order/after-sales-return/list') {
          this.addTab('退换货订单', to.path)
        } else if (to.path === '/main/order/after-sales-return/add') {
          this.addTab('新建退换货订单', to.path)
        } else if (/^\/main\/order\/after-sales-return\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('退换货订单详情', to.path)
        } else if (to.path === '/main/order/product/list') {
          this.addTab('产品订单', to.path)
        } else if (to.path === '/main/order/product/add') {
          if (to.query.copyFrom) {
            this.addTab('复制新建产品订单', tabPath)
          } else {
            this.addTab('新建产品订单', tabPath)
          }
        } else if (/^\/main\/order\/product\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('产品订单详情', to.path)
        } else if (/^\/main\/order\/product\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑产品订单', to.path)
        } else if (to.path === '/main/order/bundle-product/list') {
          this.addTab('组合产品订单', to.path)
        } else if (to.path === '/main/order/bundle-product/add') {
          this.addTab('新建组合产品订单', to.path)
        } else if (/^\/main\/order\/bundle-product\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('组合产品订单详情', to.path)
        } else if (/^\/main\/order\/bundle-product\/edit\/[^/]+$/.test(to.path)) {
          this.addTab('编辑组合产品订单', to.path)
        } else if (to.path === '/main/order/service-package/list') {
          this.addTab('服务包订单', to.path)
        } else if (to.path === '/main/order/service-package/add') {
          this.addTab('新建服务包订单', to.path)
        } else if (/^\/main\/order\/service-package\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('服务包订单详情', to.path)
        } else if (to.path === '/main/order/sp-shipment-application/list') {
          this.addTab('服务包订单发货申请', to.path)
        } else if (/^\/main\/order\/sp-shipment-application\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('发货申请详情', to.path)
        } else if (to.path === '/main/order/sales-outbound/list') {
          this.addTab('销售出库单明细', to.path)
        } else if (/^\/main\/order\/sales-outbound\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('出库单详情', to.path)
        } else if (/^\/main\/order\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('订单详情', to.path)
        } else if (to.path === '/main/iot/device') {
          this.addTab('设备管理', to.path)
        } else if (/^\/main\/iot\/device\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('设备详情', to.path)
        } else if (to.path === '/main/iot/contract/create') {
          this.addTab('新增合约', to.path)
        } else if (/^\/main\/iot\/contract\/remove-device\/[^/]+$/.test(to.path)) {
          this.addTab('移除设备', to.path)
        } else if (/^\/main\/iot\/contract\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('合约详情', to.path)
        } else if (to.path === '/main/workorder/service/create') {
          this.addTab('创建工单', to.path)
        } else if (/^\/main\/workorder\/service\/edit\/\d+$/.test(to.path)) {
          this.addTab('编辑工单', to.path)
        } else if (/^\/main\/workorder\/service\/detail\/[^/]+$/.test(to.path)) {
          this.addTab('工单详情', to.path)
        } else if (to.path === '/main/workorder/service') {
          this.addTab('服务工单', to.path)
        } else if (to.path === '/main/workorder/engineer-level') {
          this.addTab('工程师等级', to.path)
        } else if (to.path === '/main/workorder/dispatcher') {
          this.addTab('执行人管理', to.path)
        } else if (to.path === '/main/workorder/aftersale-category') {
          this.addTab('售后类别管理', to.path)
        } else if (to.path === '/main/workorder/complaint-category') {
          this.addTab('投诉类别管理', to.path)
        } else if (to.path === '/main/system/message-list') {
          this.addTab('消息通知', to.path)
        } else if (to.path === '/main/system/message-config') {
          this.addTab('通知设置', to.path)
        } else {
          const exists = this.openTabs.find(t => t.path === to.path)
          if (exists) {
            this.activeTab = to.path
          } else if (to.path !== '/main/dashboard') {
            // 刷新后恢复当前路由的页签：从 MENU_ROUTE_MAP 反查菜单名称
            const label = Object.keys(MENU_ROUTE_MAP).find(k => MENU_ROUTE_MAP[k] === to.path)
            if (label) this.addTab(label, to.path)
          }
        }
        // Tab 数量超限导致 addTab 失败时，回退到上一个标签页
        if (this.activeTab !== tabPath && prevTab && prevTab !== tabPath) {
          this.$nextTick(() => this.$router.replace(prevTab))
        }
      },
      immediate: true,
    },
  },
  methods: {
    loadUserInfo() {
      try {
        const raw = localStorage.getItem('user')
        if (raw) this.userInfo = JSON.parse(raw)
      } catch { /* ignore */ }
    },
    /** 页面加载时用后端最新权限刷新 localStorage 缓存，避免旧登录会话权限过期 */
    async refreshPermissions() {
      try {
        const userRes = await getCurrentUser()
        if (userRes && userRes.code === 0 && userRes.data) {
          const oldUser = localStorage.getItem('user')
          if (oldUser) {
            const cached = JSON.parse(oldUser)
            // 仅更新权限列表，保留登录时写入的其他字段
            cached.permissionIds = userRes.data.permissionIds || []
            // 兼容旧会话：登录响应补下发 loginAccount 前的老缓存缺该字段，在此回写
            if (!cached.loginAccount && userRes.data.loginAccount) {
              cached.loginAccount = userRes.data.loginAccount
            }
            localStorage.setItem('user', JSON.stringify(cached))
          }
        }
      } catch { /* 静默失败，不影响主流程 */ }
    },
    handleSidebarCommand(cmd) {
      if (cmd === 'logout') this.handleLogout()
      else if (cmd === 'profile') this.openProfile()
    },
    handleTopbarCommand(cmd) {
      if (cmd === 'logout') this.handleLogout()
      else if (cmd === 'profile') this.openProfile()
      else if (cmd === 'changePassword') this.openChangePassword()
    },
    async handleLogout() {
      try {
        await this.$confirm('确定要退出登录吗？', '退出确认', {
          confirmButtonText: '退出',
          cancelButtonText: '取消',
          type: 'warning',
        })
      } catch { return }
      try { await logout() } catch { /* ignore */ }
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('qek_auto_login')
      localStorage.removeItem('qek_refresh_token')
      this.$router.push('/login')
    },
    async openProfile() {
      this.profileVisible = true
      try {
        const res = await getCurrentUser()
        if (res.code === 0 && res.data) this.profileInfo = res.data
      } catch {
        this.profileInfo = { ...this.userInfo }
      }
    },
    openChangePassword() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.pwdDialogVisible = true
      this.$nextTick(() => this.$refs.pwdForm && this.$refs.pwdForm.clearValidate())
    },
    async submitChangePassword() {
      try { await this.$refs.pwdForm.validate() } catch { return }
      this.pwdLoading = true
      try {
        const res = await changePassword(this.pwdForm)
        if (res.code === 0) {
          this.$message.success('密码修改成功，请重新登录')
          this.pwdDialogVisible = false
          this.handleLogout()
        }
      } catch (e) {
        this.$message.error(e.message || '密码修改失败')
      } finally {
        this.pwdLoading = false
      }
    },
    onNavEnter(key, e) {
      this.clearTimer()
      this.navItemRect = e.currentTarget.getBoundingClientRect()
      this.panelKey = key
      this.$nextTick(() => this.adjustPanelPosition())
    },
    adjustPanelPosition() {
      const panel = this.$refs.megaPanel
      if (!panel || !this.navItemRect) return
      const navCenter = this.navItemRect.top + this.navItemRect.height / 2
      const panelH = panel.offsetHeight
      const viewH = window.innerHeight
      const margin = 8
      let top = navCenter - panelH / 2
      if (top < margin) top = margin
      if (top + panelH > viewH - margin) top = viewH - margin - panelH
      if (top < margin) top = margin
      this.navItemRect = { ...this.navItemRect, _top: top }
    },
    onNavLeave() {
      this.startTimer()
    },
    onPanelEnter() {
      this.clearTimer()
    },
    onPanelLeave() {
      this.startTimer()
    },
    onMainEnter() {
      this.startTimer()
    },
    startTimer() {
      this.closeTimer = setTimeout(() => {
        this.panelKey = null
      }, 150)
    },
    clearTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    goHome() {
      this.activeKey = 'home'
      this.panelKey = null
      this.activeTab = '/main/dashboard'
      if (this.$route.path !== '/main/dashboard') {
        this.$router.push('/main/dashboard')
      }
    },
    onItemClick(itemName) {
      const target = MENU_ROUTE_MAP[itemName] || '/main/dashboard'
      if (target.startsWith('/screen/')) {
        this.panelKey = null
        window.open(this.$router.resolve(target).href, '_blank')
        return
      }
      this.activeKey = this.panelKey
      this.panelKey = null
      if (this.$route.path !== target) {
        this.$router.push(target)
      } else {
        this.activeTab = target
      }
    },
    /**
     * 将当前激活的 Tab 横向滚动到可视区域（Tab 数量超出容器宽度时）
     */
    scrollActiveTabIntoView() {
      const container = this.$refs.pageTabs
      if (!container) return
      const el = container.querySelector('.page-tab.active')
      if (!el) return
      const padding = 16
      const cRect = container.getBoundingClientRect()
      const eRect = el.getBoundingClientRect()
      let left = container.scrollLeft
      if (eRect.left < cRect.left + padding) {
        left = container.scrollLeft - (cRect.left + padding - eRect.left)
      } else if (eRect.right > cRect.right - padding) {
        left = container.scrollLeft + (eRect.right - cRect.right + padding)
      } else {
        return
      }
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ left, behavior: 'smooth' })
      } else {
        container.scrollLeft = left
      }
    },
    addTab(label, path) {
      const exists = this.openTabs.find(t => t.path === path)
      if (!exists) {
        if (this.openTabs.length >= MAX_TABS) {
          this.$message.warning(`最多只能打开 ${MAX_TABS} 个标签页，请关闭一些后再试`)
          return false
        }
        this.openTabs.push({ label, path, icon: null, closable: true })
      }
      this.activeTab = path
      return true
    },
    /** 与 addTab 使用的 path 对齐（复制新建用 fullPath） */
    tabPathForRoute(route) {
      if (route.path === '/main/order/product/add' && route.query && route.query.copyFrom) {
        return route.fullPath
      }
      if (route.path === '/main/product/add' && route.query && route.query.copyFrom) {
        return route.fullPath
      }
      if (route.path === '/main/product/service-package/add' && route.query && route.query.copyFrom) {
        return route.fullPath
      }
      return route.path
    },
    isKeepAliveRoute(route) {
      return route.name === 'ProductOrderAdd' || route.name === 'ProductOrderEdit'
    },
    pageInstanceKey(route) {
      const tabPath = this.tabPathForRoute(route)
      const refresh = this.pageRefreshMap[tabPath] || 0
      return `${tabPath}__${refresh}`
    },
    bumpPageRefresh(tabPath) {
      if (!tabPath) return
      this.pageRefreshMap = {
        ...this.pageRefreshMap,
        [tabPath]: (this.pageRefreshMap[tabPath] || 0) + 1,
      }
    },
    /**
     * BUG-353：该页签路径是否就是当前正在展示的路由（兼容复制新建的 fullPath 形态）。
     */
    isCurrentRoutePath(path) {
      return !!path && (this.$route.path === path || this.$route.fullPath === path)
    },
    /**
     * BUG-353：关闭页签时递增刷新键（本意＝丢弃该路径的 keep-alive 缓存实例，见 pageRefreshMap 注释）。
     * 但 pageInstanceKey 同时就是该页的组件 :key：若在「该页仍在展示中」时就递增，会把正在离开的页面
     * 就地销毁重建，而重建实例的 async created 可能在路由已切走之后才读到 $route.params.id，于是拿
     * 别的页面的 id 去发请求（表现为误报「服务包不存在」「订单不存在」并把跳转劫持回列表）。
     * 故：当前展示页一律**等路由真正离开之后**再递增；后台页签维持原行为（即时递增）。
     */
    bumpPageRefreshOnClose(tabPath) {
      if (!tabPath) return
      if (!this.isCurrentRoutePath(tabPath)) {
        this.bumpPageRefresh(tabPath)
        return
      }
      const stop = this.$watch(
        () => this.$route.path,
        () => {
          if (this.isCurrentRoutePath(tabPath)) return
          stop()
          this.bumpPageRefresh(tabPath)
        }
      )
    },
    switchTab(tab) {
      this.activeTab = tab.path
      if (tab.path === '/main/dashboard') {
        this.activeKey = 'home'
      }
      if (this.$route.path !== tab.path && this.$route.fullPath !== tab.path) {
        this.$router.push(tab.path)
      }
    },
    releaseListViewStateOnClose(paths) {
      const list = Array.isArray(paths) ? paths.filter(Boolean) : []
      list.forEach(p => clearListViewStateByTabPath(p))
    },
    /** 关闭页签但不跳转路由（提交成功后由页面自行跳转） */
    closeTabByPathSilent(path) {
      if (!path) return
      const idx = this.openTabs.findIndex(t => t.path === path)
      if (idx === -1) {
        // BUG-353：同 closeTab —— 当前展示页需等路由离开后再递增刷新键
        this.bumpPageRefreshOnClose(path)
        return
      }
      const tab = this.openTabs[idx]
      if (!tab.closable) return
      this.releaseListViewStateOnClose([tab.path])
      this.openTabs.splice(idx, 1)
      // BUG-353：当前展示页等路由离开后再递增刷新键，避免把正在离开的页面就地重建（见该方法注释）
      this.bumpPageRefreshOnClose(tab.path)
      if (this.activeTab === tab.path) {
        const last = this.openTabs[this.openTabs.length - 1]
        this.activeTab = last ? last.path : '/main/dashboard'
        if (this.activeTab === '/main/dashboard') this.activeKey = 'home'
      }
    },
    closeTab(tab) {
      const idx = this.openTabs.findIndex(t => t.path === tab.path)
      if (idx === -1 || !tab.closable) return
      this.releaseListViewStateOnClose([tab.path])
      this.openTabs.splice(idx, 1)
      // BUG-353：当前展示页等路由离开后再递增刷新键，避免把正在离开的页面就地重建（见该方法注释）
      this.bumpPageRefreshOnClose(tab.path)
      if (this.activeTab === tab.path) {
        const last = this.openTabs[this.openTabs.length - 1]
        this.activeTab = last.path
        if (last.path === '/main/dashboard') {
          this.activeKey = 'home'
        }
        if (this.$route.path !== last.path && this.$route.fullPath !== last.path) {
          this.$router.push(last.path)
        }
      }
    },
    openCtxMenu(e, tab) {
      this.ctxMenu = { visible: true, x: e.clientX, y: e.clientY, tab }
      if (this._ctxClose) {
        document.removeEventListener('click', this._ctxClose)
        document.removeEventListener('contextmenu', this._ctxClose)
      }
      const self = this
      this._ctxClose = function () {
        self.ctxMenu.visible = false
        document.removeEventListener('click', self._ctxClose)
        document.removeEventListener('contextmenu', self._ctxClose)
        self._ctxClose = null
      }
      setTimeout(() => {
        document.addEventListener('click', this._ctxClose)
        document.addEventListener('contextmenu', this._ctxClose)
      }, 50)
    },
    ctxRefresh() {
      const tab = this.ctxMenu.tab
      if (!tab) return
      this.ctxMenu.visible = false
      if (this.activeTab !== tab.path) {
        this.switchTab(tab)
      }
      this.bumpPageRefresh(tab.path)
    },
    ctxCloseCurrent() {
      const tab = this.ctxMenu.tab
      this.ctxMenu.visible = false
      if (tab && tab.closable) this.closeTab(tab)
    },
    ctxCloseOthers() {
      const tab = this.ctxMenu.tab
      this.ctxMenu.visible = false
      const keepPath = tab && tab.path
      const closedPaths = this.openTabs.filter(t => t.closable && t.path !== keepPath).map(t => t.path)
      this.releaseListViewStateOnClose(closedPaths)
      this.openTabs.filter(t => t.closable && t.path !== keepPath).forEach(t => {
        this.bumpPageRefresh(t.path)
      })
      this.openTabs = this.openTabs.filter(t => !t.closable || t.path === keepPath)
      if (tab) this.switchTab(tab)
    },
    ctxCloseAll() {
      this.ctxMenu.visible = false
      const closedPaths = this.openTabs.filter(t => t.closable).map(t => t.path)
      this.releaseListViewStateOnClose(closedPaths)
      this.openTabs.filter(t => t.closable).forEach(t => {
        this.bumpPageRefresh(t.path)
      })
      this.openTabs = this.openTabs.filter(t => !t.closable)
      const home = this.openTabs[0]
      this.activeTab = home.path
      this.activeKey = 'home'
      if (this.$route.path !== home.path) {
        this.$router.push(home.path)
      }
    },
    onSearchInput() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) {
        this.searchResults = this.searchIndex.slice()
        this.searchDropdownVisible = true
        this.searchHighlight = -1
        return
      }
      this.searchResults = this.searchIndex.filter(item =>
        item.name.toLowerCase().includes(q)
      )
      this.searchDropdownVisible = true
      this.searchHighlight = -1
    },
    onSearchEnter() {
      if (this.searchHighlight >= 0 && this.searchResults[this.searchHighlight]) {
        this.selectSearchResult(this.searchResults[this.searchHighlight])
      } else if (this.searchResults.length === 1) {
        this.selectSearchResult(this.searchResults[0])
      }
    },
    onSearchArrowDown() {
      if (this.searchResults.length) {
        this.searchHighlight = (this.searchHighlight + 1) % this.searchResults.length
        this.scrollHighlightIntoView()
      }
    },
    onSearchArrowUp() {
      if (this.searchResults.length) {
        this.searchHighlight = this.searchHighlight <= 0
          ? this.searchResults.length - 1
          : this.searchHighlight - 1
        this.scrollHighlightIntoView()
      }
    },
    scrollHighlightIntoView() {
      this.$nextTick(() => {
        const box = this.$refs.searchBox
        const el = box && box.querySelector('.search-item.active')
        if (el) el.scrollIntoView({ block: 'nearest' })
      })
    },
    onSearchFocus() {
      if (!this.searchQuery.trim()) {
        this.searchResults = this.searchIndex.slice()
      }
      this.searchDropdownVisible = true
    },
    onSearchBlur() {
      setTimeout(() => {
        this.searchDropdownVisible = false
        this.searchHighlight = -1
      }, 200)
    },
    selectSearchResult(item) {
      this.searchQuery = ''
      this.searchResults = []
      this.searchDropdownVisible = false
      this.searchHighlight = -1
      if (item.path.startsWith('/screen/')) {
        window.open(this.$router.resolve(item.path).href, '_blank')
        return
      }
      if (this.$route.path !== item.path) {
        this.$router.push(item.path)
      } else {
        this.activeTab = item.path
      }
    },
    // ── 消息通知 ──
    async loadMsgUnreadCount() {
      if (!this.showMessageBell) return
      try {
        const res = await getUnreadCount()
        if (res.code === 0 && res.data) {
          this.msgUnreadTotal = res.data.total || 0
        }
      } catch { /* ignore */ }
    },
    async onBellPopoverShow() {
      this.msgPopoverLoading = true
      try {
        const res = await getRecentUnread(5)
        if (res.code === 0) {
          this.msgRecentList = res.dataList || []
        }
      } catch { /* ignore */ }
      this.msgPopoverLoading = false
    },
    async onPopoverMsgClick(item) {
      if (item.readStatus === 'unread') {
        try {
          await markAsRead(item.id)
          item.readStatus = 'read'
          this.msgUnreadTotal = Math.max(0, this.msgUnreadTotal - 1)
        } catch { /* ignore */ }
      }
      if (item.bizUrl) {
        this.$router.push(item.bizUrl)
      }
    },
    formatDateTime,
    goMessageList() {
      if (this.$route.path !== '/main/system/message-list') {
        this.$router.push('/main/system/message-list')
      } else {
        this.activeTab = '/main/system/message-list'
      }
    },
  },
}
</script>

<style scoped>
/* ─── Root ─── */
.layout-wrap {
  display: flex;
  height: 100vh;
  overflow: hidden;
  font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  background: #f5f7fa;
  position: relative;
}

/* ─── Sidebar ─── */
.sidebar {
  width: 150px;
  min-width: 150px;
  height: 100vh;
  background: #0f2244;
  display: flex;
  flex-direction: column;
  z-index: 200;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
}

.sidebar-logo {
  height: 60px;
  min-height: 60px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.logo-name {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  white-space: nowrap;
}

.logo-ver {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.3;
}

.nav-list {
  flex: 1;
  padding: 6px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-list::-webkit-scrollbar {
  width: 4px;
}
.nav-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.nav-item {
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 12px 0 14px;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  gap: 8px;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.9);
}

.nav-item.active {
  background: #1677ff;
  color: #fff;
}

.nav-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.nav-label {
  flex: 1;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-arrow {
  font-size: 11px;
  opacity: 0.5;
  transition: transform 0.2s ease, opacity 0.15s;
  flex-shrink: 0;
}

.nav-item.panel-open .nav-arrow {
  transform: rotate(90deg);
  opacity: 0.9;
}

.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding: 10px 0;
}

.user-bar {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  gap: 8px;
  cursor: pointer;
  transition: background 0.12s;
}

.user-bar:hover {
  background: rgba(255, 255, 255, 0.06);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1677ff, #69b1ff);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-name {
  flex: 1;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}

/* ─── Mega panel ─── */
.mega-panel {
  position: fixed;
  left: 150px;
  top: 0;
  min-width: 260px;
  width: auto;
  max-height: calc(100vh - 16px);
  background: #fff;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.12);
  z-index: 180;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.panel-head {
  height: 48px;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  font-size: 15px;
  font-weight: 600;
  color: #1677ff;
  border-bottom: 1px solid #f0f0f0;
  background: #fafcff;
  white-space: nowrap;
  border-radius: 8px 8px 0 0;
}

.panel-head-icon {
  font-size: 17px;
}

/* ── Horizontal group row ── */
.panel-body {
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 8px 16px 16px;
  gap: 0;
}

.panel-group {
  min-width: 160px;
  flex: 0 0 auto;
  padding: 0 16px 0 0;
  margin-right: 8px;
  border-right: 1px solid #f0f2f5;
}

.panel-group:last-child {
  border-right: none;
  margin-right: 0;
}

/* ── Group heading with left color bar ── */
.group-title {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 2px 0 10px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
  white-space: nowrap;
}

.group-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  background: #1677ff;
  border-radius: 2px;
  flex-shrink: 0;
}

/* ── Items with background (3rd-level look) ── */
.group-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.panel-item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
  border-radius: 6px;
  background: #f4f6fb;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
  user-select: none;
}

.panel-item:hover {
  background: #dbeafe;
  color: #1677ff;
}

/* Panel slide animation */
.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: transform 0.18s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.18s ease;
}

.panel-slide-enter,
.panel-slide-leave-to {
  transform: translateX(-16px);
  opacity: 0;
}

/* ─── Main wrapper ─── */
.main-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* Top bar */
.top-bar {
  height: 60px;
  min-height: 60px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 10;
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.system-name {
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 18px;
  padding: 0 12px;
  height: 32px;
  width: 200px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.search-box.is-active,
.search-box:focus-within {
  border-color: #1677ff;
  box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.12);
  background: #fff;
}

.search-icon {
  font-size: 14px;
  color: #bfbfbf;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #303133;
  min-width: 0;
}

.search-input::placeholder {
  color: #c0c4cc;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid #ebeef5;
  max-height: 320px;
  overflow-y: auto;
  z-index: 999;
}

.search-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s;
}

.search-item:hover,
.search-item.active {
  background: #f0f7ff;
}

.search-item + .search-item {
  border-top: 1px solid #f0f2f5;
}

.search-item-name {
  font-size: 13px;
  color: #303133;
}

.search-item-group {
  font-size: 11px;
  color: #909399;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-left: 12px;
}

.search-empty {
  padding: 20px 14px;
  text-align: center;
  font-size: 13px;
  color: #909399;
}

.search-tip {
  padding: 8px 14px 6px;
  font-size: 12px;
  color: #909399;
}

.search-group-title {
  padding: 8px 14px 4px;
  font-size: 11px;
  color: #909399;
  background: #fafafa;
}

.tb-badge {
  line-height: 1;
}

.tb-icon {
  font-size: 18px;
  color: #606266;
  cursor: pointer;
  transition: color 0.12s;
}

.tb-icon:hover {
  color: #1677ff;
}

.user-dropdown {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s;
}

.user-dropdown:hover {
  background: #f5f7fa;
}

.user-avatar-sm {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1677ff, #69b1ff);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-label {
  font-size: 13px;
  color: #303133;
}

/* Page tabs */
.page-tabs {
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  padding: 0 16px;
  display: flex;
  align-items: center;
  height: 38px;
  gap: 4px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
}

.page-tabs::-webkit-scrollbar {
  height: 0;
}

.page-tab {
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  height: 30px;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
  cursor: pointer;
  transition: all 0.12s;
  border: 1px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
}

.page-tab:hover {
  color: #1677ff;
  background: #f0f7ff;
}

.page-tab.active {
  color: #1677ff;
  background: #e8f3ff;
  border-color: #bae0ff;
  font-weight: 500;
}

.tab-close {
  margin-left: 6px;
  font-size: 12px;
  color: #909399;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.tab-close:hover {
  background: #ff4d4f;
  color: #fff;
}

/* Tab context menu */
.tab-ctx-menu {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid #ebeef5;
  padding: 4px 0;
  min-width: 130px;
}

.ctx-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}

.ctx-item:hover {
  background: #f0f7ff;
  color: #1677ff;
}

.ctx-item i {
  font-size: 14px;
}

/* Content */
.content-area {
  flex: 1;
  overflow: hidden;
  background: #f4f6f9;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.content-area-inner {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.content-area::-webkit-scrollbar {
  width: 6px;
}

.content-area::-webkit-scrollbar-thumb {
  background: #d9d9d9;
  border-radius: 3px;
}
</style>

<!-- 铃铛弹出框渲染在 body 层，需要非 scoped 样式 -->
<style>
.message-popover {
  padding: 0 !important;
}

.msg-pop-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2d3d;
  border-bottom: 1px solid #f0f2f5;
}

.msg-pop-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.msg-pop-empty {
  padding: 32px 0;
  text-align: center;
  font-size: 13px;
  color: #c0c4cc;
}

.msg-pop-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.12s;
}

.msg-pop-item:hover {
  background: #f0f7ff;
}

.msg-pop-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1677ff;
  margin-top: 7px;
  flex-shrink: 0;
}

.msg-pop-body {
  flex: 1;
  min-width: 0;
}

.msg-pop-content {
  font-size: 13px;
  color: #303133;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 2px;
}

.msg-pop-time {
  font-size: 12px;
  color: #909399;
}

.msg-pop-footer {
  padding: 10px 0;
  text-align: center;
  font-size: 13px;
  color: #1677ff;
  cursor: pointer;
  border-top: 1px solid #f0f2f5;
  transition: background 0.12s;
}

.msg-pop-footer:hover {
  background: #f0f7ff;
}
</style>
